import { useEffect, useMemo, useRef, useState } from 'react';

import Icon from '@shared/common/icon/Icon';
import { UiButton } from './UiButton';

function formatValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseValue(value) {
  if (!value) return null;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function formatLabel(value) {
  const date = parseValue(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

function monthTitle(date) {
  return new Intl.DateTimeFormat('ru-RU', {
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function addMonths(date, diff) {
  return new Date(date.getFullYear(), date.getMonth() + diff, 1);
}

function buildCalendarDays(viewDate) {
  const first = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - ((first.getDay() + 6) % 7));

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return date;
  });
}

export function UiDateRangePicker({ label, from, to, onChange, className = '' }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const startDate = parseValue(from);
  const [viewDate, setViewDate] = useState(startDate || new Date());
  const days = useMemo(() => buildCalendarDays(viewDate), [viewDate]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocumentMouseDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onDocumentKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentMouseDown);
    document.addEventListener('keydown', onDocumentKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown);
      document.removeEventListener('keydown', onDocumentKeyDown);
    };
  }, [open]);

  const selectDate = (date) => {
    const value = formatValue(date);
    if (!from || (from && to)) {
      onChange({ from: value, to: '' });
      return;
    }
    onChange(value < from ? { from: value, to: from } : { from, to: value });
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={`ui-field ui-date-range-picker ${className}`.trim()}>
      {label ? <span className="ui-field__label">{label}</span> : null}
      <button
        type="button"
        className="ui-control ui-date-range-picker__trigger"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <span>{formatLabel(from)} — {formatLabel(to)}</span>
        <span className="ui-date-range-picker__icon" aria-hidden="true">
          <Icon icon="Calendar/Calendar" width={18} height={17} color="currentColor" />
        </span>
      </button>

      {open ? (
        <div className="ui-date-range-picker__calendar">
          <div className="ui-date-range-picker__header">
            <UiButton variant="ghost" size="sm" type="button" aria-label="Предыдущий месяц" onClick={() => setViewDate((date) => addMonths(date, -1))}>‹</UiButton>
            <strong>{monthTitle(viewDate)}</strong>
            <UiButton variant="ghost" size="sm" type="button" aria-label="Следующий месяц" onClick={() => setViewDate((date) => addMonths(date, 1))}>›</UiButton>
          </div>
          <div className="ui-date-range-picker__weekdays">
            {['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'].map((day) => <span key={day}>{day}</span>)}
          </div>
          <div className="ui-date-range-picker__days">
            {days.map((date) => {
              const value = formatValue(date);
              return (
                <button
                  key={value}
                  type="button"
                  className={[
                    'ui-date-range-picker__day',
                    date.getMonth() !== viewDate.getMonth() ? 'is-outside' : '',
                    from && to && value > from && value < to ? 'is-in-range' : '',
                    value === from || value === to ? 'is-selected' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => selectDate(date)}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
